/**
 * S3 direct-upload service.
 *
 * Architecture:
 *   1. Client asks our backend for a presigned PUT URL via
 *      /histopathology/s3/presigned-upload/.
 *   2. Browser PUTs the file directly to S3 using that URL (no Django middleman).
 *   3. Caller then POSTs the returned `s3_key` + metadata to whichever endpoint
 *      persists the document row (e.g. OutsourceResultUploadView with the
 *      metadata-only mode: {result_detail_id, s3_documents:[{s3_key, doc_name}]}).
 *
 * Why XHR instead of axios for the PUT:
 *   axios on a presigned URL would attach our Authorization header, which S3
 *   rejects because the URL is already signed. XMLHttpRequest gives us a clean
 *   header set + native `upload.onprogress` events for accurate progress.
 */

import axios from "axios";
import { getBaseUrl } from "./RestAPIService";
import { Serviceurls } from "../API/Serviceurls";

const ALLOWED_TYPES = new Set([
  "application/pdf",
  "image/jpeg", "image/jpg", "image/png", "image/gif", "image/bmp", "image/webp",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
]);

const MAX_BYTES = 25 * 1024 * 1024; // 25 MB

/**
 * Validate file size + type before asking for a presigned URL.
 * Cheaper than letting S3 reject and gives a clear UX error.
 */
function validate(file, contentType) {
  if (!file) throw new Error("No file provided");
  const size = file.size ?? 0;
  if (size <= 0) throw new Error("File is empty");
  if (size > MAX_BYTES) {
    const mb = Math.round(size / (1024 * 1024));
    throw new Error(`File is ${mb}MB. Maximum allowed is ${MAX_BYTES / (1024 * 1024)}MB.`);
  }
  const ct = (contentType || "").toLowerCase();
  if (!ALLOWED_TYPES.has(ct)) {
    throw new Error(`File type not supported: ${contentType || "unknown"}`);
  }
}

async function getPresignedUrl({ fileName, contentType, fileSize, folderScope, resultDetailId, filenamePrefix }) {
  const token = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("AccessToken") : null;
  const response = await axios.create({
    baseURL: getBaseUrl(),
    headers: {
      "Content-Type": "application/json",
      Authorization: token ? `Token ${token}` : "",
      "ngrok-skip-browser-warning": "true",
    },
  }).post("/" + Serviceurls.HISTOPATHOLOGY_S3_PRESIGNED_UPLOAD, {
    file_name: fileName,
    content_type: contentType,
    file_size: fileSize,
    folder_scope: folderScope || "result",
    result_detail_id: resultDetailId ?? null,
    filename_prefix: filenamePrefix || "DOC_",
  });
  const payload = response?.data?.data ?? response?.data;
  if (!payload?.url || !payload?.key) {
    throw new Error(response?.data?.message || "Backend did not return a valid presigned URL");
  }
  return payload;
}

/**
 * PUT the file/blob directly to S3 using the presigned URL.
 * Resolves when S3 confirms (HTTP 2xx).
 */
function putToS3(presignedUrl, fileOrBlob, contentType, onProgress, abortSignal) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", presignedUrl, true);
    xhr.setRequestHeader("Content-Type", contentType || "application/octet-stream");

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`S3 upload failed: HTTP ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error("Network error during S3 upload"));
    xhr.ontimeout = () => reject(new Error("S3 upload timed out"));

    if (abortSignal) {
      abortSignal.addEventListener("abort", () => {
        try { xhr.abort(); } catch {}
        reject(new DOMException("Upload aborted", "AbortError"));
      });
    }

    xhr.send(fileOrBlob);
  });
}

/**
 * Upload a File object directly to S3.
 *
 * @param {Object}   args
 * @param {File}     args.file             - File from an <input type="file">
 * @param {string}   [args.folderScope]    - Folder key (e.g. "result"); default "result"
 * @param {number}   [args.resultDetailId] - Nests under folder/<id>/ on S3
 * @param {string}   [args.filenamePrefix] - Filename prefix (default "DOC_")
 * @param {(n:number)=>void} [args.onProgress] - Called with 0..100 during PUT
 * @param {AbortSignal}       [args.abortSignal]
 *
 * @returns {Promise<{key: string}>}
 */
export async function uploadToS3({ file, folderScope, resultDetailId, filenamePrefix = "DOC_", onProgress, abortSignal }) {
  validate(file, file.type);

  const presigned = await getPresignedUrl({
    fileName: file.name,
    contentType: file.type,
    fileSize: file.size,
    folderScope,
    resultDetailId,
    filenamePrefix,
  });

  await putToS3(presigned.url, file, file.type, onProgress, abortSignal);

  return { key: presigned.key };
}

export const S3_MAX_BYTES = MAX_BYTES;
export const S3_MAX_MB = MAX_BYTES / (1024 * 1024);
